import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('monchery_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para tratar erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('monchery_token');
      // Rotas públicas (loja virtual) não devem ser sequestradas para o login
      // do admin quando o token expira — a loja funciona sem autenticação.
      const path = window.location.pathname;
      const rotaPublica = path.startsWith('/loja') || path === '/login';
      if (!rotaPublica) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ── Auth ───────────────────────────────────────────────────
export const authApi = {
  login: (email: string, senha: string) => api.post('/auth/login', { email, senha }),
  me: () => api.get('/auth/me'),
  setup: (nome: string, email: string, senha: string) => api.post('/auth/setup', { nome, email, senha }),
};

// ── Dashboard ──────────────────────────────────────────────
export const dashboardApi = {
  central: () => api.get('/dashboard/central'),
  financeiro: (params?: { mes?: number; ano?: number }) => api.get('/dashboard/financeiro', { params }),
  projecaoCaixa: (params?: { mes?: number; ano?: number }) => api.get('/dashboard/projecao-caixa', { params }),
};

// ── Clientes ───────────────────────────────────────────────
export const clienteApi = {
  listar: (params?: { busca?: string; page?: number; limit?: number }) =>
    api.get('/clientes', { params }),
  buscarPorId: (id: string) => api.get(`/clientes/${id}`),
  buscarPorTelefone: (telefone: string) => api.get(`/clientes/telefone/${telefone}`),
  criar: (data: any) => api.post('/clientes', data),
  atualizar: (id: string, data: any) => api.put(`/clientes/${id}`, data),
  remover: (id: string) => api.delete(`/clientes/${id}`),
};

// ── Pedidos ────────────────────────────────────────────────
export const pedidoApi = {
  listar: (params?: { situacao?: string; busca?: string; page?: number; limit?: number }) =>
    api.get('/pedidos', { params }),
  debitos: (params?: any) => api.get('/pedidos/debitos', { params }),
  buscarPorId: (id: string) => api.get(`/pedidos/${id}`),
  criar: (data: any) => api.post('/pedidos', data),
  atualizar: (id: string, data: any) => api.put(`/pedidos/${id}`, data),
  atualizarSituacao: (id: string, situacao: string) =>
    api.patch(`/pedidos/${id}/situacao`, { situacao }),
  marcarImpresso: (id: string) => api.patch(`/pedidos/${id}/impresso`),
  remover: (id: string) => api.delete(`/pedidos/${id}`),
};

// ── Recebimentos (baixas de pedidos e créditos avulsos) ────
export const recebimentoApi = {
  criar: (data: any) => api.post('/recebimentos', data),
  listarPorPedido: (pedidoId: string) => api.get(`/recebimentos/pedido/${pedidoId}`),
  remover: (id: string) => api.delete(`/recebimentos/${id}`),
};

// ── Itens Padrão (campos dinâmicos do pedido) ──────────────
export const campoApi = {
  listar: () => api.get('/campos'),
  criar: (data: any) => api.post('/campos', data),
  atualizar: (id: string, data: any) => api.put(`/campos/${id}`, data),
  remover: (id: string) => api.delete(`/campos/${id}`),
};

// ── Configurações gerais ───────────────────────────────────
export const configuracaoApi = {
  ler: (chave: string) => api.get(`/configuracoes/${chave}`),
  salvar: (chave: string, valor: any, descricao?: string) =>
    api.put(`/configuracoes/${chave}`, { valor, descricao }),
};

// ── Costura ────────────────────────────────────────────────
export const costuraApi = {
  listarCostureiras: () => api.get('/costura/costureiras'),
  criarCostureira: (data: any) => api.post('/costura/costureiras', data),
  listarLotes: (params?: any) => api.get('/costura/lotes', { params }),
  buscarLote: (id: string) => api.get(`/costura/lotes/${id}`),
  criarLote: (data: any) => api.post('/costura/lotes', data),
  receberLote: (id: string, data: any) => api.patch(`/costura/lotes/${id}/receber`, data),
  marcarImpresso: (id: string) => api.patch(`/costura/lotes/${id}/impresso`),
  listarPagamentos: (params?: any) => api.get('/costura/pagamentos', { params }),
  registrarPagamento: (data: any) => api.post('/costura/pagamentos', data),
};

// ── Financeiro ─────────────────────────────────────────────
export const financeiroApi = {
  listar: (params?: any) => api.get('/financeiro', { params }),
  buscarPorId: (id: string) => api.get(`/financeiro/${id}`),
  criar: (data: any) => api.post('/financeiro', data),
  atualizar: (id: string, data: any) => api.put(`/financeiro/${id}`, data),
  remover: (id: string) => api.delete(`/financeiro/${id}`),
  entradaRapida: (data: any) => api.post('/financeiro/entrada-rapida', data),
  sugerir: (texto: string) => api.get('/financeiro/sugerir', { params: { texto } }),
  resumoDia: () => api.get('/financeiro/resumo-dia'),
};

// ── Referências ────────────────────────────────────────────
export const referenciaApi = {
  listar: (params?: any) => api.get('/referencias', { params }),
  sugerir: (tipo: string, q: string) => api.get('/referencias/sugerir', { params: { tipo, q } }),
  criar: (data: any) => api.post('/referencias', data),
  atualizar: (id: string, valor: string) => api.put(`/referencias/${id}`, { valor }),
  remover: (id: string) => api.delete(`/referencias/${id}`),
};

// ── Loja virtual (público) ─────────────────────────────────
export const lojaApi = {
  catalogo: () => api.get('/loja/catalogo'),
  criarPedido: (data: any) => api.post('/loja/pedidos', data),
  statusPedido: (id: string) => api.get(`/loja/pedidos/${id}`),
};

// ── Área do cliente da loja ("Minha conta") ────────────────
// Instância separada: token e redirecionamento próprios, para não
// conflitar com a sessão do painel administrativo.
export const apiCliente = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiCliente.interceptors.request.use((config) => {
  const token = localStorage.getItem('monchery_cliente_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiCliente.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('monchery_cliente_token');
      localStorage.removeItem('monchery_cliente');
      if (!window.location.pathname.startsWith('/loja/conta/login')) {
        window.location.href = '/loja/conta/login';
      }
    }
    return Promise.reject(error);
  }
);

export const contaApi = {
  registro: (data: { nome: string; email: string; telefone: string; senha: string }) =>
    apiCliente.post('/conta/registro', data),
  verificarEmail: (email: string, codigo: string) =>
    apiCliente.post('/conta/verificar-email', { email, codigo }),
  reenviarCodigo: (email: string) => apiCliente.post('/conta/reenviar-codigo', { email }),
  login: (email: string, senha: string) => apiCliente.post('/conta/login', { email, senha }),
  esqueciSenha: (email: string) => apiCliente.post('/conta/esqueci-senha', { email }),
  redefinirSenha: (email: string, codigo: string, novaSenha: string) =>
    apiCliente.post('/conta/redefinir-senha', { email, codigo, novaSenha }),
  me: () => apiCliente.get('/conta/me'),
  atualizarPerfil: (data: { nome: string; telefone: string }) =>
    apiCliente.put('/conta/perfil', data),
  trocarSenha: (senhaAtual: string, novaSenha: string) =>
    apiCliente.post('/conta/trocar-senha', { senhaAtual, novaSenha }),
  pedidos: () => apiCliente.get('/conta/pedidos'),
  criarPedido: (data: any) => apiCliente.post('/conta/pedidos', data),
  pagarPedido: (id: string) => apiCliente.post(`/conta/pedidos/${id}/pagar`),
};

// ── Catálogo da loja (admin) ───────────────────────────────
export const catalogoApi = {
  // Produtos
  listarProdutos: () => api.get('/catalogo/produtos'),
  criarProduto: (data: any) => api.post('/catalogo/produtos', data),
  atualizarProduto: (id: string, data: any) => api.put(`/catalogo/produtos/${id}`, data),
  removerProduto: (id: string) => api.delete(`/catalogo/produtos/${id}`),
  // Cores
  listarCores: () => api.get('/catalogo/cores'),
  criarCor: (data: any) => api.post('/catalogo/cores', data),
  atualizarCor: (id: string, data: any) => api.put(`/catalogo/cores/${id}`, data),
  removerCor: (id: string) => api.delete(`/catalogo/cores/${id}`),
  // Tamanhos
  listarTamanhos: () => api.get('/catalogo/tamanhos'),
  criarTamanho: (data: any) => api.post('/catalogo/tamanhos', data),
  atualizarTamanho: (id: string, data: any) => api.put(`/catalogo/tamanhos/${id}`, data),
  removerTamanho: (id: string) => api.delete(`/catalogo/tamanhos/${id}`),
  // Estampas
  listarEstampas: () => api.get('/catalogo/estampas'),
  criarEstampa: (data: any) => api.post('/catalogo/estampas', data),
  atualizarEstampa: (id: string, data: any) => api.put(`/catalogo/estampas/${id}`, data),
  removerEstampa: (id: string) => api.delete(`/catalogo/estampas/${id}`),
  // Pedidos da loja
  listarPedidos: (params?: any) => api.get('/catalogo/pedidos', { params }),
  atualizarStatusPedido: (id: string, status: string) =>
    api.patch(`/catalogo/pedidos/${id}/status`, { status }),
  // Upload de imagem (estampas e produtos)
  uploadImagem: (arquivo: File) => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    return api.post('/catalogo/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};
